export type ParsedContactUrl =
  | {
      platform: "telegram";
      kind: "dm" | "group" | "channel" | "unknown";
      canonicalUrl: string;
      handle?: string;
      raw: string;
    }
  | {
      platform: "x";
      canonicalUrl: string;
      handle: string;
      raw: string;
    }
  | {
      platform: "linkedin";
      canonicalUrl: string;
      raw: string;
    }
  | {
      platform: "unknown";
      raw: string;
    };

export interface ContactUrlParserOptions {
  bareHandlePlatform?: "telegram" | "x";
}

const HANDLE_PATTERN = /^[A-Za-z0-9_]{1,32}$/;
const TELEGRAM_RESERVED = new Set(["c", "s", "joinchat"]);
const X_RESERVED = new Set([
  "home",
  "explore",
  "notifications",
  "messages",
  "i",
  "intent",
  "share",
  "search",
  "settings",
]);

function normalizeRaw(input: string): string {
  return input.trim().replace(/[\u200B-\u200D\uFEFF]/g, "");
}

function ensureHttps(value: string): string {
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
}

function stripTrailingPunctuation(value: string): string {
  return value.replace(/[),.;\]\s]+$/g, "");
}

function normalizeHandle(value: string): string | null {
  const handle = value.trim().replace(/^@+/, "").replace(/\/$/, "");
  if (!HANDLE_PATTERN.test(handle)) return null;
  return handle;
}

function parseUrl(value: string): URL | null {
  try {
    return new URL(ensureHttps(value));
  } catch {
    return null;
  }
}

function parseTelegramUrl(raw: string, url: URL): ParsedContactUrl | null {
  const hostname = url.hostname.toLowerCase().replace(/^www\./, "");
  if (hostname !== "t.me" && hostname !== "telegram.me") return null;

  const segments = url.pathname.split("/").filter(Boolean);
  const firstSegment = segments[0] ?? "";
  const handle = normalizeHandle(firstSegment);
  const canonicalUrl = stripTrailingPunctuation(`https://t.me/${segments.join("/")}`);

  if (firstSegment.startsWith("+")) {
    return { platform: "telegram", kind: "group", canonicalUrl, raw };
  }

  if (firstSegment === "c") {
    return { platform: "telegram", kind: "channel", canonicalUrl, raw };
  }

  if (handle && !TELEGRAM_RESERVED.has(handle.toLowerCase())) {
    return {
      platform: "telegram",
      kind: "dm",
      canonicalUrl: `https://t.me/${handle}`,
      handle,
      raw,
    };
  }

  return { platform: "telegram", kind: "unknown", canonicalUrl, raw };
}

function parseTelegramScheme(raw: string): ParsedContactUrl | null {
  if (!/^tg:\/\//i.test(raw)) return null;

  try {
    const url = new URL(raw);
    const domain = url.searchParams.get("domain");
    const handle = domain ? normalizeHandle(domain) : null;
    if (handle) {
      return {
        platform: "telegram",
        kind: "dm",
        canonicalUrl: `https://t.me/${handle}`,
        handle,
        raw,
      };
    }
  } catch {
    return { platform: "telegram", kind: "unknown", canonicalUrl: raw, raw };
  }

  return { platform: "telegram", kind: "unknown", canonicalUrl: raw, raw };
}

function parseXUrl(raw: string, url: URL): ParsedContactUrl | null {
  const hostname = url.hostname.toLowerCase().replace(/^www\./, "");
  if (hostname !== "x.com" && hostname !== "twitter.com") return null;

  const handle = normalizeHandle(url.pathname.split("/").filter(Boolean)[0] ?? "");
  if (!handle || X_RESERVED.has(handle.toLowerCase())) return null;

  return { platform: "x", canonicalUrl: `https://x.com/${handle}`, handle, raw };
}

function parseLinkedInUrl(raw: string, url: URL): ParsedContactUrl | null {
  const hostname = url.hostname.toLowerCase().replace(/^www\./, "");
  if (hostname !== "linkedin.com") return null;

  return {
    platform: "linkedin",
    canonicalUrl: stripTrailingPunctuation(url.toString()),
    raw,
  };
}

export function parseContactUrl(
  input: string,
  options: ContactUrlParserOptions = {}
): ParsedContactUrl {
  const raw = normalizeRaw(input);
  if (!raw) return { platform: "unknown", raw: input };

  const bareHandle = raw.startsWith("@") ? normalizeHandle(raw) : null;
  if (bareHandle) {
    if (options.bareHandlePlatform === "telegram") {
      return {
        platform: "telegram",
        kind: "dm",
        canonicalUrl: `https://t.me/${bareHandle}`,
        handle: bareHandle,
        raw,
      };
    }

    if (options.bareHandlePlatform === "x") {
      return {
        platform: "x",
        canonicalUrl: `https://x.com/${bareHandle}`,
        handle: bareHandle,
        raw,
      };
    }

    return { platform: "unknown", raw };
  }

  const telegramScheme = parseTelegramScheme(raw);
  if (telegramScheme) return telegramScheme;

  const url = parseUrl(raw);
  if (!url) return { platform: "unknown", raw };

  return (
    parseTelegramUrl(raw, url) ??
    parseXUrl(raw, url) ??
    parseLinkedInUrl(raw, url) ?? { platform: "unknown", raw }
  );
}

export function isBareAmbiguousHandle(input: string): boolean {
  const raw = normalizeRaw(input);
  return raw.startsWith("@") && Boolean(normalizeHandle(raw));
}
